import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend } from 'chart.js';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarDays, Copy, ExternalLink, Info, Square, SquaresUnite } from 'lucide-react';
import useHistoricalData from '../../hooks/useHistoricalData';
import useErrorsHistory from '../../hooks/useErrorsHistory';
import FilterPanel from '../../components/Filters/FilterPanel';
import JiraTicketModal from '../JiraTicketForm/JiraTicketModal';
import './SlidingWindow.css';
import '../Filters/DateRangePicker.css';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x; };
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const formatShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const PRESETS = [
  { key: '7d',  label: '7D',  start: () => addDays(new Date(), -7) },
  { key: '15d', label: '15D', start: () => addDays(new Date(), -15) },
  { key: '30d', label: '30D', start: () => addDays(new Date(), -30) },
  { key: '60d', label: '60D', start: () => addDays(new Date(), -60) },
];

const SlidingWindow = ({ isOpen, onClose, content }) => {
  const rawContent = React.isValidElement(content) ? content.props.children : content;
  const parsedContent = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;

  const testId = parsedContent?.testId;
  const isCsqXp = parsedContent?.isCsqXp === true;
  const { pathname } = useLocation();
  const isMobile = pathname.startsWith('/mobile');
  const platformLabel = isCsqXp ? 'CSQ XP' : (isMobile ? 'Legacy' : 'OneApp');
  const githubRepo = pathname.startsWith('/mobile')
    ? 'ContentSquare/dc_cs-mobile-integration-test'
    : 'ContentSquare/app-frontends';

  const [preset, setPreset] = useState('30d');
  const [startDate, setStartDate] = useState(toIso(addDays(new Date(), -30)));
  const [endDate, setEndDate] = useState(toIso(startOfDay(new Date())));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const datePickerRef = useRef(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState('All Environments');

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handleClick = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [popoverOpen]);

  const handlePresetChange = (_e, next) => {
    if (!next) return;
    if (next === 'custom') {
      setDraftStart(null);
      setDraftEnd(null);
      setPopoverOpen(true);
      setPreset('custom');
      return;
    }
    const p = PRESETS.find(p => p.key === next);
    const today = startOfDay(new Date());
    setPreset(next);
    setStartDate(toIso(p.start()));
    setEndDate(toIso(today));
    setPopoverOpen(false);
  };

  const handleCustomClick = () => {
    if (preset === 'custom') setPopoverOpen(open => !open);
  };

  const handleCalendarChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
    if (start && end) {
      setStartDate(toIso(startOfDay(start)));
      setEndDate(toIso(startOfDay(end)));
      setPopoverOpen(false);
    }
  };

  const customLabel =
    preset === 'custom' && startDate && endDate
      ? `${formatShort(new Date(startDate))} – ${formatShort(new Date(endDate))}`
      : 'Custom';

  const [copied, setCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategories] = useState({});

  const handleCopyUuid = () => {
    navigator.clipboard.writeText(testId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { historicalData, loading: loadingHistoricalData, error: errorHistoricalData } = useHistoricalData(isOpen, parsedContent, startDate, endDate, testId, selectedEnvironment, isCsqXp);
  const { errorHistory, loading: loadingErrors, error: errorFetchingErrors } = useErrorsHistory(testId, isCsqXp);
  const hasBrowserstackSessions = errorHistory?.some(error => error.browserstackSessionUrl) ?? false;

  const chartData = {
    labels: historicalData ? historicalData.map(item => item.date) : [],
    datasets: [
      {
        label: 'Passed',
        data: historicalData ? historicalData.map(item => item.results.passed) : [],
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        fill: true,
      },
      {
        label: 'Failed',
        data: historicalData ? historicalData.map(item => item.results.failed) : [],
        borderColor: '#F44336',
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        fill: true,
      },
      {
        label: 'Skipped',
        data: historicalData ? historicalData.map(item => item.results.skipped) : [],
        borderColor: '#9E9E9E',
        backgroundColor: 'rgba(158, 158, 158, 0.2)',
        fill: true,
      },
    ],
  };

  const handleCreateJiraTicket = () => {
    setIsModalOpen(true);
  };

  return (
    <div className={`sliding-window ${isOpen ? 'open' : ''}`}>
      <button onClick={onClose} className="close-button">×</button>
      <div>
        <div className="slider-filters-container">
          <div className="date-range-picker" ref={datePickerRef}>
            <ToggleButtonGroup
              className="date-range-toggle-group"
              value={preset}
              exclusive
              onChange={handlePresetChange}
              size="small"
            >
              {PRESETS.map(({ key, label }) => (
                <ToggleButton key={key} value={key} className="date-range-toggle">
                  {label}
                </ToggleButton>
              ))}
              <ToggleButton
                value="custom"
                className="date-range-toggle date-range-custom"
                onClick={handleCustomClick}
              >
                <CalendarDays size={14} style={{ marginRight: 4 }} />
                {customLabel}
              </ToggleButton>
            </ToggleButtonGroup>
            {popoverOpen && (
              <div className="date-range-popover">
                <DatePicker
                  selected={draftStart}
                  onChange={handleCalendarChange}
                  startDate={draftStart}
                  endDate={draftEnd}
                  maxDate={new Date()}
                  selectsRange
                  inline
                />
              </div>
            )}
          </div>
          <FilterPanel
            fields={[{
              key: 'env',
              label: 'Environment',
              options: ['All Environments', 'Local', 'Staging', 'Production'],
              defaultValue: 'All Environments',
            }]}
            values={{ env: selectedEnvironment }}
            onApply={({ env }) => setSelectedEnvironment(env)}
          />
        </div>
        <div className="test-title">
          <h2>{parsedContent?.testName || 'Test name not available'}</h2>
          {testId && (
            <div className="test-uuid-row">
              <div className="test-platform-pill">
                {isCsqXp ? <SquaresUnite size={14} /> : <Square size={14} />}
                <span className="test-platform-label">{platformLabel}</span>
              </div>
              <div className="test-uuid-pill">
                <span className="test-uuid-label">UUID: </span>
                <span className="test-uuid-value">{testId}</span>
              </div>
              <div className="test-uuid-copy-btn" onClick={handleCopyUuid}>
                <Copy size={13} />
                {copied ? <span className="test-uuid-copy-label">Copied!</span> : <span className="test-uuid-copy-label">Copy</span>}
              </div>
            </div>
          )}
        </div>
        <div className="line-chart-container">
          <h3>Historical Data</h3>
          {loadingHistoricalData ? (
            <div className="line-chart-box flashing loading" style={{ height: '80%' }}></div>
          ) : errorHistoricalData ? (
            <div className="line-chart-box flashing error" style={{ height: '80%' }}></div>
          ) : historicalData && historicalData.length > 0 ? (
            <div className="line-chart-box">
            <Line data={chartData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: true,
                  text: 'Test Results Over Time',
                },
              },
            }}/>
            </div>
          ) : null}
        </div>
        <div className="last-errors-container">
          <h3>Error History</h3>
          {loadingErrors ? (
            <div className="last-errors-table loading flashing" style={{height: '80%'}}></div>
          ) : errorFetchingErrors ? (
            <div className="last-errors-table error flashing" style={{height: '80%'}}></div>
          ) : errorHistory && errorHistory.length > 0 ? (
            <table className="last-errors-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Error</th>
                  <th>Failure reason</th>
                  <th>Failure Reason AI</th>
                  {hasBrowserstackSessions && <th>Browserstack Session</th>}
                  <th>GitHub Run</th>
                </tr>
              </thead>
              <tbody>
                {errorHistory.map((error, index) => {
                  const parts = error.runSessionId.split('-');
                  const runId = parts.find(part => /^\d{8,}$/.test(part)) || parts.slice(-2, -1)[0];
                  const githubRunUrl = `https://github.com/${githubRepo}/actions/runs/${runId}`;
                  const currentValue = selectedCategories[index] || error.failureReasonCategory;
                  const shouldShowGreen = currentValue && currentValue !== "";
                  
                  return (
                    <tr className="last-errors-table-row" key={index}>
                      <td>{new Date(error.date).toLocaleString()}</td>
                      <td>{error.testError}</td>
                      <td className={shouldShowGreen ? 'has-failure-reason' : 'missing-failure-reason'}>
                        <select
                          value={selectedCategories[index] || error.failureReasonCategory || ""}
                          onChange={(e) => {
                            // Prevent selection by reverting to original value
                            e.target.value = selectedCategories[index] || error.failureReasonCategory || "";
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="" disabled>missing</option>
                          <option value="Bug">Bug</option>
                          <option value="Data Generation">Data Generation</option>
                          <option value="Environment">Environment</option>
                          <option value="Framework">Framework</option>
                          <option value="Test implementation">Test implementation</option>
                        </select>
                      </td>
                      <td>{error.failureReasonCategoryByAi || "-"}</td>
                      {hasBrowserstackSessions && (
                        <td>
                          {error.browserstackSessionUrl ? (
                            <a href={error.browserstackSessionUrl} target="_blank" rel="noopener noreferrer" className="last-errors-table-link">
                              <ExternalLink size={12} />
                              Session
                            </a>
                          ) : (
                            <span className="bs-session-missing"><Info size={12} />missing</span>
                          )}
                        </td>
                      )}
                      <td>
                        <a href={githubRunUrl} target="_blank" rel="noopener noreferrer" className="last-errors-table-link">
                          <ExternalLink size={12} />
                          {runId}
                        </a>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
        <div className="action-buttons-container">
          <button
            className="create-ticket-button"
            onClick={handleCreateJiraTicket}
          >
            Create Jira ticket
          </button>
        </div>
        <JiraTicketModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </div>
    </div>
  );
};

export default SlidingWindow; 
